import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, LoadingState } from "@/command/ccUi";

export default function Settings() {
  const { can } = useCommand();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const canManage = can("settings", "MANAGE");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cc/settings").then(({ data }) => setS(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...s };
      payload.approval_threshold = Number(payload.approval_threshold) || 0;
      payload.renewal_alert_days = Number(payload.renewal_alert_days) || 14;
      await api.put("/cc/settings", payload); toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading || !s) return <LoadingState label="Loading settings…" />;

  return (
    <div className="max-w-2xl">
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Settings</h1>
      <p className="text-subtle mt-1 mb-6">Command Center preferences and defaults.</p>

      <Panel testid="settings-panel">
        <div className="grid grid-cols-2 gap-5">
          <Field label="Fiscal year start">
            <select className={inputCls} value={s.fiscal_year_start || "April"} onChange={(e) => setS({ ...s, fiscal_year_start: e.target.value })} data-testid="settings-fiscal" disabled={!canManage}>
              {["January", "April", "July", "October"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Default currency">
            <select className={inputCls} value={s.default_currency || "INR"} onChange={(e) => setS({ ...s, default_currency: e.target.value })} data-testid="settings-currency" disabled={!canManage}>
              {["INR", "USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Timezone"><input className={inputCls} value={s.timezone || ""} onChange={(e) => setS({ ...s, timezone: e.target.value })} data-testid="settings-timezone" disabled={!canManage} /></Field>
          <Field label="Approval threshold (₹)"><input type="number" className={inputCls} value={s.approval_threshold ?? ""} onChange={(e) => setS({ ...s, approval_threshold: e.target.value })} data-testid="settings-threshold" disabled={!canManage} /></Field>
          <Field label="Renewal alert (days ahead)"><input type="number" className={inputCls} value={s.renewal_alert_days ?? 14} onChange={(e) => setS({ ...s, renewal_alert_days: e.target.value })} data-testid="settings-renewal-days" disabled={!canManage} /></Field>
          <div className="col-span-2">
            <label className="flex items-center gap-3 text-sm text-ink">
              <input type="checkbox" checked={!!s.notifications_enabled} onChange={(e) => setS({ ...s, notifications_enabled: e.target.checked })} data-testid="settings-notifications" disabled={!canManage} />
              Enable Command Center notifications
            </label>
          </div>
        </div>
        {canManage && (
          <div className="flex justify-end mt-6">
            <CCButton onClick={save} testid="settings-save">Save settings</CCButton>
          </div>
        )}
        {!canManage && <p className="text-xs text-subtle mt-4">Only Super Admins / Founders can change settings.</p>}
      </Panel>
    </div>
  );
}
